/**
 * Stage progress visualisation — 9 dots for purchase, 6 dots for refinance.
 * The active dot is filled with the brand teal; completed dots are filled with
 * `success`; locked dots show as outlined; the active dot is rendered larger.
 * If the property is idle, the active dot is amber.
 */

import { Box, useTheme } from '@mui/material';

import type { PropertyView } from '@/types/PropertyView';
import { isStageActive, isStageCompleted } from './propertyCardConstants';

interface PropertyStageProgressProps {
  property: PropertyView;
  size?: 'sm' | 'md';
}

const ACTIVE_TEAL = 'var(--stage-active)';
const COMPLETED_TEAL = 'var(--stage-completed)';
const LOCKED_GREY = 'var(--stage-locked)';
const IDLE_AMBER = 'var(--stage-idle)';

export const PropertyStageProgress = ({
  property,
  size = 'md',
}: PropertyStageProgressProps) => {
  const _theme = useTheme();
  const dotSize = size === 'sm' ? 8 : 10;
  const activeDotSize = size === 'sm' ? 12 : 14;
  const gap = size === 'sm' ? 6 : 8;

  return (
    <Box
      role="img"
      aria-label={`Stage ${property.stage} of ${property.visibleStages.length}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: `${gap}px`,
      }}
    >
      {property.visibleStages.map((s) => {
        const isActive = isStageActive(s, property);
        const isCompleted = isStageCompleted(s, property);
        const baseColor = isActive
          ? property.isIdle
            ? IDLE_AMBER
            : ACTIVE_TEAL
          : isCompleted
          ? COMPLETED_TEAL
          : LOCKED_GREY;
        return (
          <Box
            key={s}
            sx={{
              width: isActive ? activeDotSize : dotSize,
              height: isActive ? activeDotSize : dotSize,
              borderRadius: '50%',
              backgroundColor:
                isCompleted || isActive ? baseColor : 'transparent',
              border:
                isCompleted || isActive ? 'none' : `1px solid ${LOCKED_GREY}`,
              transition: 'all 0.15s ease',
            }}
          />
        );
      })}
    </Box>
  );
};
